import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Activity,
  FileText,
  Settings,
  LogOut,
  Shield,
} from 'lucide-react';
import { cn } from '../../lib/cn';
import { useAuth } from '../../auth';

interface NavItem {
  label: string;
  path: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
}

const navItems: NavItem[] = [
  { label: 'Overview', path: '/', icon: LayoutDashboard },
  { label: 'Users', path: '/users', icon: Users },
  { label: 'Platform Health', path: '/health', icon: Activity },
  { label: 'Audit Log', path: '/audit', icon: FileText },
  { label: 'Settings', path: '/settings', icon: Settings },
];

/**
 * Admin dashboard sidebar navigation.
 * Compact design with clear navigation hierarchy.
 */
export function Sidebar() {
  const { user, logout } = useAuth();

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 bottom-0 w-sidebar',
        'bg-surface',
        'border-r border-border',
        'flex flex-col',
        'z-30'
      )}
    >
      {/* Logo */}
      <div className="h-14 px-4 flex items-center gap-2 border-b border-border">
        <div className="w-8 h-8 rounded-lg bg-action-container flex items-center justify-center">
          <Shield size={18} className="text-action" />
        </div>
        <div>
          <span className="text-card-title text-text">
            PerMile
          </span>
          <span className="text-caption text-text-muted ml-1.5">
            Admin
          </span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto custom-scrollbar">
        {navItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.path === '/'}
            className={({ isActive }) =>
              cn(
                'nav-item',
                isActive && 'nav-item-active'
              )
            }
          >
            <item.icon size={18} />
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>

      {/* User section */}
      <div className="p-3 border-t border-border">
        <div className="px-3 py-2 mb-2">
          <p className="text-body-sm font-medium text-text truncate">
            {user?.email || user?.username}
          </p>
          <p className="text-caption text-text-muted">
            Administrator
          </p>
        </div>
        <button
          onClick={logout}
          className={cn(
            'nav-item w-full text-left',
            'text-loss hover:bg-loss/10 hover:text-loss'
          )}
        >
          <LogOut size={18} />
          <span>Sign Out</span>
        </button>
      </div>
    </aside>
  );
}
